import { useCallback, useEffect, useRef, useState } from 'react'
import { VodPlayer, type Position } from './VodPlayer'
import { Header } from './Header'
import { parsePlayerParams } from './playerParams'
import { projectTimezone, setPlayerPosition } from './playerUrl'
import { presetForUrl, setPresetForUrl } from './cameras'
import { localDay } from './time'
import './page.css'

const timezoneFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  timeZoneName: 'short',
})

function timezoneAt(date: Date) {
  return timezoneFormatter
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')!.value
}

export default function App({ youtubeEndpoint }: { youtubeEndpoint?: string } = {}) {
  const [initial] = useState(() =>
    parsePlayerParams(new URLSearchParams(location.search)),
  )
  const embed = location.pathname.replace(/\/$/, '') === '/embed'
  const [preset] = useState(() => presetForUrl(new URL(location.href)))
  const [defaultExpanded] = useState(
    () => !embed && !new URLSearchParams(location.search).has('at'),
  )
  const position = useRef<Position | null>(null)
  const [day, setDay] = useState(
    () =>
      initial.day ??
      (initial.at ? localDay(Date.parse(initial.at), projectTimezone) : ''),
  )
  const [timezone, setTimezone] = useState(() =>
    timezoneAt(initial.at ? new Date(initial.at) : new Date()),
  )
  const updateUrl = useCallback(() => {
    // Safari can overwrite address-bar edits when replaceState runs while the
    // browser chrome has focus. Keep tracking playback, but defer URL writes.
    if (!position.current || !document.hasFocus()) return
    const url = new URL(location.href)
    setPresetForUrl(url, preset)
    setPlayerPosition(url, position.current.camera, Date.parse(position.current.at))
    if (url.href !== location.href) history.replaceState(history.state, '', url)
  }, [preset])
  const onPositionChange = useCallback(
    (next: Position) => {
      const previous = position.current
      position.current = next
      setDay(next.day)
      setTimezone(timezoneAt(new Date(next.at)))
      if (!previous || previous.camera !== next.camera || previous.day !== next.day)
        updateUrl()
    },
    [updateUrl],
  )
  useEffect(() => {
    if (embed) return
    const timer = setInterval(updateUrl, 2500)
    window.addEventListener('focus', updateUrl)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', updateUrl)
    }
  }, [embed, updateUrl])

  const home = new URL('/', location.href)
  setPresetForUrl(home, preset)

  return (
    <>
      {!embed && (
        <Header
          timezone={timezone}
          home={home.pathname + home.search}
          day={day}
          youtubeEndpoint={youtubeEndpoint}
        />
      )}
      <VodPlayer
        {...initial}
        preset={preset}
        defaultExpanded={defaultExpanded}
        showLogo={embed}
        controls={embed ? initial.controls : 'full'}
        keyboard="global"
        className={embed ? 'embed-player' : 'standalone-player'}
        onPositionChange={embed ? undefined : onPositionChange}
      />
    </>
  )
}
